import { notificationService } from './notification.service';
import { Notification, NotificationType, NotificationCategory, NotificationImportance } from '../../types/notification';
import { Task } from './tasks.service';
import { Guest } from './guests.service';
import { Vendor } from './vendors.service';

class AutoNotificationService {
  /**
   * Génère une notification lorsqu'une tâche change de statut
   */
  async notifyTaskStatusChange(
    weddingId: string, 
    task: Task, 
    oldStatus: string, 
    newStatus: string
  ): Promise<string | null> {
    // Ne pas notifier si le statut n'a pas changé
    if (oldStatus === newStatus) return null;

    let title = '';
    let message = '';
    let type: NotificationType = 'task';
    let importance: NotificationImportance = 'medium';

    // Déterminer le message en fonction du changement de statut
    if (newStatus === 'completed') {
      title = '✅ Tâche terminée';
      message = `La tâche "${task.title}" a été marquée comme terminée.`;
      type = 'success';
      importance = 'medium';
    } else if (newStatus === 'in_progress' && oldStatus === 'todo') {
      title = '🔄 Tâche en cours';
      message = `La tâche "${task.title}" est maintenant en cours.`;
      type = 'info';
      importance = 'low';
    } else if (newStatus === 'todo' && oldStatus === 'completed') {
      title = '⚠️ Tâche rouverte';
      message = `La tâche "${task.title}" a été rouverte.`;
      type = 'warning';
      importance = 'medium';
    } else if (newStatus === 'overdue') {
      title = '⚠️ Tâche en retard';
      message = `La tâche "${task.title}" est maintenant en retard.`;
      type = 'warning';
      importance = 'high';
    } else {
      // Changement de statut générique
      title = '🔄 Statut de tâche modifié';
      message = `La tâche "${task.title}" est passée de "${oldStatus}" à "${newStatus}".`;
      type = 'info';
      importance = 'low';
    }

    // Créer la notification
    return await notificationService.createNotification({
      weddingId,
      title,
      message,
      type,
      category: 'tasks',
      importance,
      link: `/tasks?id=${task.id}`,
      metadata: {
        taskId: task.id,
        oldStatus,
        newStatus
      }
    });
  }

  /**
   * Génère une notification lorsqu'un certain nombre d'invités est atteint
   */
  async notifyGuestMilestone(
    weddingId: string, 
    guestCount: number, 
    confirmedCount: number
  ): Promise<string | null> {
    let title = '';
    let message = '';
    let type: NotificationType = 'guest';
    let importance: NotificationImportance = 'medium';
    let shouldNotify = false;

    // Vérifier les jalons pour le nombre total d'invités
    if (guestCount === 10) {
      title = '🎉 10 invités ajoutés';
      message = 'Vous avez ajouté 10 invités à votre liste. Continuez comme ça !';
      shouldNotify = true;
    } else if (guestCount === 50) {
      title = '🎉 50 invités ajoutés';
      message = 'Vous avez atteint 50 invités ! Votre liste d\'invités prend forme.';
      shouldNotify = true;
    } else if (guestCount === 100) {
      title = '🎉 100 invités ajoutés';
      message = 'Félicitations ! Vous avez atteint 100 invités dans votre liste.';
      shouldNotify = true;
      importance = 'high';
    } else if (guestCount % 50 === 0 && guestCount > 100) {
      title = `🎉 ${guestCount} invités ajoutés`;
      message = `Vous avez maintenant ${guestCount} invités dans votre liste. Impressionnant !`;
      shouldNotify = true;
    }

    // Vérifier les jalons pour le nombre d'invités confirmés
    if (confirmedCount === 10) {
      title = '✅ 10 invités confirmés';
      message = 'Vous avez maintenant 10 invités confirmés pour votre mariage !';
      shouldNotify = true;
    } else if (confirmedCount === 50) {
      title = '✅ 50 invités confirmés';
      message = 'Vous avez atteint 50 invités confirmés ! Votre mariage prend forme.';
      shouldNotify = true;
      importance = 'high';
    } else if (confirmedCount === 100) {
      title = '✅ 100 invités confirmés';
      message = 'Félicitations ! Vous avez 100 invités confirmés pour votre mariage.';
      shouldNotify = true;
      importance = 'high';
    } else if (confirmedCount % 50 === 0 && confirmedCount > 100) {
      title = `✅ ${confirmedCount} invités confirmés`;
      message = `Vous avez maintenant ${confirmedCount} invités confirmés pour votre mariage !`;
      shouldNotify = true;
    }

    // Si aucun jalon n'est atteint, ne pas notifier
    if (!shouldNotify) return null;

    // Créer la notification
    return await notificationService.createNotification({
      weddingId,
      title,
      message,
      type,
      category: 'guests',
      importance,
      link: '/guests',
      metadata: {
        totalGuests: guestCount,
        confirmedGuests: confirmedCount
      }
    });
  }

  /**
   * Génère une notification lorsqu'un prestataire change de statut
   */
  async notifyVendorStatusChange(
    weddingId: string, 
    vendor: Vendor, 
    oldStatus: string, 
    newStatus: string = vendor.status
  ): Promise<string | null> {
    if (!weddingId || !vendor) return null;

    let title = '';
    let message = '';
    let type: NotificationType = 'info';
    let importance: NotificationImportance = 'medium';

    // Déterminer le type de notification en fonction du changement de statut
    if (newStatus === 'hired') {
      title = '🎉 Nouveau prestataire engagé';
      message = `${vendor.name} a été engagé comme ${this.getVendorCategoryName(vendor.category)}.`;
      type = 'success';
      importance = 'high';
    } else if (newStatus === 'deposit_paid') {
      title = '💰 Acompte payé';
      message = `L'acompte pour ${vendor.name} a été payé.`;
      type = 'success';
      importance = 'medium';
    } else if (newStatus === 'completed') {
      title = '✅ Prestation terminée';
      message = `La prestation de ${vendor.name} est terminée.`;
      type = 'success';
      importance = 'medium';
    } else if (newStatus === 'cancelled') {
      title = '❌ Prestation annulée';
      message = `La prestation de ${vendor.name} a été annulée.`;
      type = 'warning';
      importance = 'high';
    } else {
      // Changement de statut générique
      title = '🔄 Statut de prestataire modifié';
      message = `Le statut de ${vendor.name} est passé de "${this.getVendorStatusName(oldStatus)}" à "${this.getVendorStatusName(newStatus)}".`;
      type = 'info';
      importance = 'low';
    }

    // Créer la notification
    return await notificationService.createNotification({
      weddingId,
      title,
      message,
      type,
      category: 'vendors',
      importance,
      link: `/vendors?id=${vendor.id}`,
      metadata: {
        vendorId: vendor.id,
        vendorName: vendor.name,
        vendorCategory: vendor.category,
        oldStatus: oldStatus || 'unknown',
        newStatus: newStatus || 'unknown'
      }
    });
  }

  /**
   * Obtient le nom lisible d'une catégorie de prestataire
   */
  private getVendorCategoryName(category: string): string {
    const categoryNames: Record<string, string> = {
      venue: 'lieu de réception',
      catering: 'traiteur',
      photography: 'photographe',
      videography: 'vidéaste',
      music: 'musicien',
      florist: 'fleuriste',
      cake: 'pâtissier',
      dress: 'boutique de robe',
      suit: 'tailleur',
      hair_makeup: 'coiffeur/maquilleur',
      decoration: 'décorateur',
      transportation: 'service de transport',
      other: 'prestataire'
    };

    return categoryNames[category] || 'prestataire';
  }

  /**
   * Obtient le nom lisible d'un statut de prestataire
   */
  private getVendorStatusName(status: string): string {
    const statusNames: Record<string, string> = {
      researching: 'En recherche',
      contacted: 'Contacté',
      meeting_scheduled: 'Rendez-vous planifié',
      quote_received: 'Devis reçu',
      hired: 'Engagé',
      deposit_paid: 'Acompte payé',
      completed: 'Terminé',
      cancelled: 'Annulé'
    };

    return statusNames[status] || status;
  }
}

export const autoNotificationService = new AutoNotificationService(); 